import { useState, useEffect } from 'react';
import { doFetch } from '@/util/fetch';
import { getIdToken } from '@/util/authentication';
import { apiServer } from '@/util/apiServer';

/**
 * Hook to fetch Stripe payment intent (clientSecret) for Apple Pay and Google Pay.
 * Reuses the same endpoint as StripePaymentForm.
 */
export function useStripePaymentIntent(orderUuid: string, paymentMethodType?: string) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orderUuid) return;

    const fetchIntent = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getIdToken();

        if (!token) {
          setError("Session missing. Please logout and login again.");
          setLoading(false);
          return;
        }

        const response = await doFetch(`${apiServer ?? ''}/api/orders/new/${orderUuid}/action/payment/stripe/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: paymentMethodType ? JSON.stringify({ paymentMethodType }) : undefined,
        });

        if (!response.ok) {
          throw new Error('Failed to initialize payment');
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
        setAmount(data.amount);
      } catch (err: any) {
        console.error('useStripePaymentIntent error:', err);
        setError(err.message || "Could not load secure payment.");
      } finally {
        setLoading(false);
      }
    };

    fetchIntent();
  }, [orderUuid, paymentMethodType]);

  return { clientSecret, amount, error, loading };
}
